import React, { useState } from "react";
import { cards, cardVals, suffixes } from "./Vars";

export default function CardCalculator() {
  const [userInputs, setUserInputs] = useState({});
  const [userWarn, setUserWarn] = useState('');
  const [combinedResults, setCombinedResults] = useState(null);
  cards.Ultimate = [
      {
      'name':'Reaper',
      'stat': 'Attack'}]

  // Reorder cards to put Ultimate first
  const orderedCards = { Ultimate: cards.Ultimate, ...cards };
  function formatLargeNumber(num, precision = 3, t = false ) {
      
      if (num === 0) return "0";
      if (num < 1000 && !t) return num.toPrecision(precision);
    
      const tier = Math.floor(Math.log10(Math.abs(num)) / 3);
      const suffix = suffixes[tier] || `e${tier * 3}`;
      const scaled = num / Math.pow(10, tier * 3);
      if (!t) {
        return scaled.toPrecision(precision) + suffix;
      }
      return [scaled.toPrecision(precision), tier]; 
    }
  const statLookup = {};
  for (const [rarity, statsArr] of Object.entries(cardVals)) {
    statLookup[rarity] = {};
    statsArr.forEach((statItem) => {
      const [statName, vals] = Object.entries(statItem)[0];
      const merged = {};
      vals.forEach((v) => Object.assign(merged, v));
      statLookup[rarity][statName] = merged;
    });
  }

  const handleInputChange = (key, value) => {
  let num = Number(value);
  if (isNaN(num)) num = "";

  // Clamp to 0–200
  if (num < 0) num = 0;
  if (num > 200) num = 200;

  // Check for warning (101–200)
  setUserWarn((prev) => ({
    ...prev,
    [key]: num > 100 && num <= 200 ? "Card level above current max of 100 in game" : "",
  }));

  setUserInputs((prev) => ({ ...prev, [key]: num }));
};

const calcResult = (base, multi, input, statName, rarity) => {
  if (
    typeof input === "number" &&
    input >= 0 &&
    input <= 200 &&
    !isNaN(base) &&
    !isNaN(multi)
  ) {
    if (input > 100 && input <= 200) {
      // Optional: you could show a warning somewhere in the UI
      
    }
        if(statName === "Base" && rarity === "Epic" && input >= 100){
            return 5 * Math.pow(multi, input-1);
        }
        else if(input === 0 ){
            return 1
        }
        return base * Math.pow(multi, input-1);
      
    }
    return 1;
  };

  const handleCalculate = () => {
    const results = {};
    Object.entries(orderedCards).forEach(([rarity, arr]) => {
      arr.forEach((card, idx) => {
        const key = `${card.name}-${idx}`;
        const input = userInputs[key] ?? 0;
        const statsForRarity = statLookup[rarity] || {};

        let statsToShow = {};
        if (rarity === "Ultimate") {
          statsToShow = statsForRarity;
        } else {
          if (statsForRarity[card.stat]) {
            statsToShow[card.stat] = statsForRarity[card.stat];
          }
          Object.entries(statsForRarity).forEach(([statName, val]) => {
            if (!["Attack", "Health"].includes(statName) && statName !== card.stat) {
              statsToShow[statName] = val;
            }
          });
        }

        // Multiplication of all calculated stats
        Object.entries(statsToShow).forEach(([statName, { base, multi }]) => {
          if (!results[statName]) results[statName] = 1;
          results[statName] = results[statName] * calcResult(base, multi, input, statName, rarity);
        });
      });
    });
    setCombinedResults(results);
  };
  // Set all inputs to 100
const handleMax = () => {
  const newInputs = {};
  Object.entries(orderedCards).forEach(([rarity, arr]) => {
    arr.forEach((card, idx) => {
      const key = `${card.name}-${idx}`;
      newInputs[key] = 100;
    });
  });
  setUserInputs(newInputs);

  // Update warnings as well
  const newWarn = {};
  Object.keys(newInputs).forEach((key) => {
    newWarn[key] = "";
  });
  setUserWarn(newWarn);
};

const handleMaxDoubled = () => {
  const newInputs = {};
  Object.entries(orderedCards).forEach(([rarity, arr]) => {
    arr.forEach((card, idx) => {
      const key = `${card.name}-${idx}`;
      newInputs[key] = 200;
    });
  });
  setUserInputs(newInputs);
};

// Reset everything: inputs to 0, results cleared
const handleReset = () => {
  const newInputs = {};
  Object.entries(orderedCards).forEach(([rarity, arr]) => {
    arr.forEach((card, idx) => {
      const key = `${card.name}-${idx}`;
      newInputs[key] = 0;
    });
  });
  setUserInputs(newInputs);
  setUserWarn({});
  setCombinedResults(null);
};
  const rarityColors = {
    Common: "bg-gray-100 border-gray-300",
    Rare: "bg-blue-100 border-blue-400",
    Epic: "bg-purple-100 border-purple-400",
    Legendary: "bg-yellow-100 border-yellow-400",
    Ultimate: "bg-red-100 border-red-400",
  };

  // Flatten cards for display
  const allCards = Object.entries(orderedCards).flatMap(([rarity, arr]) =>
    arr.map((c, idx) => ({ ...c, rarity, idx }))
  );

  return (
    <div className="relative bg-white/60 card p-6 border rounded-md shadow-md backdrop-blur-sm transition">
      <h1 className="text-3xl font-bold mb-6 text-center">Card Calculator</h1>

      {/* Grid of cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {allCards.map((card) => {
          const key = `${card.name}-${card.idx}`;
          const input = userInputs[key] ?? "";
          const statsForRarity = statLookup[card.rarity] || {};

          // Determine which stats to show
          let statsToShow = {};
          if (card.rarity === "Ultimate") {
            statsToShow = statsForRarity;
          } else {
            if (statsForRarity[card.stat]) {
              statsToShow[card.stat] = statsForRarity[card.stat];
            }
            Object.entries(statsForRarity).forEach(([statName, val]) => {
              if (!["Attack", "Health"].includes(statName) && statName !== card.stat) {
                statsToShow[statName] = val;
              }
            });
          }

          return (
            <div
              key={key}
              className={`border rounded-xl shadow p-4 flex flex-col justify-between ${
                rarityColors[card.rarity] || "bg-white border-gray-300"
              }`}
            >
              <div className="flex justify-between mb-2">
                <span className="font-semibold text-lg">{card.name}</span>
                <span className="italic text-sm">{card.rarity}</span>
              </div>

              <input
                type="number"
                value={input}
                onChange={(e) => handleInputChange(key, e.target.value)}
                placeholder="0–200"
                className="w-full border rounded p-1 text-center mb-3"
              />
              {userWarn[key] && (
  <span className="text-red-600 text-sm">{userWarn[key]}</span>
)}

              <div className="space-y-1">
                {Object.entries(statsToShow).map(([statName, { base, multi }]) => (
                  <div key={statName} className="flex justify-between">
                    <span className="text-gray-700">{statName}</span>
                    <span className="font-bold text-blue-800">
                      {formatLargeNumber(calcResult(base, multi, input, statName, card.rarity))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-4 mb-6 justify-center">
  <button
    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-xl"
    onClick={handleCalculate}
  >
    Calculate
  </button>
  <button
    className="bg-gray-400 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-xl"
    onClick={handleReset}
  >
    Reset
  </button>
  <button
    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-xl"
    onClick={handleMax}
  >
    Max
  </button>
  <button
    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-xl"
    onClick={handleMaxDoubled}
  >
    Doubled Max
  </button>
  </div>

      {/* Combined results */}
      {combinedResults && (
        <div className="border rounded-xl p-4 bg-gray-50 shadow max-w-3xl mx-auto">
          <h2 className="text-xl font-bold mb-3 text-center">Combined Results</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 ">
            {Object.entries(combinedResults).map(([stat, value]) => (
              <div
                key={stat}
                className="bg-white border rounded p-2 flex justify-between items-center"
              >
                <span className="font-medium">{stat}</span>
                <span className="font-bold text-blue-700">{formatLargeNumber(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}